import React from 'react';
import {
  PlusOutlined,
  MenuOutlined,
  BarChartOutlined,
  FileExcelOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '../../hooks/redux';
import {
  ChatHeader as ChatHeader__Root,
  ChatHeader__Left,
  ChatHeader__Title,
  ChatHeader__SessionInfo,
  ChatHeader__Right,
  ChatHeader__HamburgerBtn,
  ChatHeader__ExcelBtn,
  ChatHeader__DiagramBtn,
  ChatHeader__NewChatBtn,
  ChatHeader__DomainSelect,
} from './ChatHeader.styles';
import {
  AgentDomainHeader__Group,
  AgentDomainHeader__Badge,
  AgentDomainHeader__DomainBtn,
} from '../AgentDomainSelector/AgentDomainSelector.styles';
import { type DomainArea } from '../AgentDomainSelector';
import { type VisualizationMode } from '../../../types/visualization.types';
import { BREAKPOINTS } from '../../../constants/breakpoints';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface ChatHeaderProps {
  onResetSession: () => void;
  onToggleSidebar?: () => void;
  /** Selected domain for agents with domain selector enabled. */
  selectedDomain?: DomainArea | null;
  /** Domain options available for the active agent. */
  availableDomains?: readonly DomainArea[];
  /** Domain options currently disabled for the active agent. */
  disabledDomains?: readonly DomainArea[];
  /** Called when user picks a domain from the header. */
  onDomainSelect?: (domain: DomainArea) => void;
  /** When provided, shows the diagram + excel buttons. */
  onVisualize?: (mode: VisualizationMode) => void;
  /** True while the visualization API call is in flight. */
  vizLoading?: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  onResetSession,
  onToggleSidebar,
  selectedDomain,
  availableDomains = [],
  disabledDomains = [],
  onDomainSelect,
  onVisualize,
  vizLoading = false,
}) => {
  const activeAgent = useAppSelector((state) => state.agent.activeAgent);
  const activeSessionId = useAppSelector((state) => state.session.activeSessionId);
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.MOBILE})`);

  const showDomainButtons = onDomainSelect != null && availableDomains.length > 0;
  const showVizButtons = onVisualize != null;

  return (
    <ChatHeader__Root>
      <ChatHeader__Left>
        {onToggleSidebar && (
          <ChatHeader__HamburgerBtn onClick={onToggleSidebar} title="Toggle sidebar">
            <MenuOutlined />
          </ChatHeader__HamburgerBtn>
        )}
        <ChatHeader__Title>
          {activeAgent?.name ?? 'AI Agent Hub'}
        </ChatHeader__Title>

        {showDomainButtons && !isMobile && (
          <AgentDomainHeader__Group>
            <AgentDomainHeader__Badge>Domain Area</AgentDomainHeader__Badge>
            {availableDomains.map((domain) => {
              const isDisabled = disabledDomains.includes(domain);
              return (
                <AgentDomainHeader__DomainBtn
                  key={domain}
                  $active={selectedDomain === domain}
                  $disabled={isDisabled}
                  onClick={() => !isDisabled && onDomainSelect(domain)}
                  aria-pressed={selectedDomain === domain}
                  aria-disabled={isDisabled}
                  title={isDisabled ? `${domain} is currently unavailable` : undefined}
                >
                  {domain}
                </AgentDomainHeader__DomainBtn>
              );
            })}
          </AgentDomainHeader__Group>
        )}
        {showDomainButtons && isMobile && (
          <ChatHeader__DomainSelect
            aria-label="Select domain area"
            value={selectedDomain ?? ''}
            onChange={(e) => onDomainSelect(e.target.value)}
          >
            {!selectedDomain && <option value="">Domain</option>}
            {availableDomains.map((domain) => {
              const isDisabled = disabledDomains.includes(domain);
              return (
                <option key={domain} value={domain} disabled={isDisabled}>
                  {domain}
                </option>
              );
            })}
          </ChatHeader__DomainSelect>
        )}
      </ChatHeader__Left>

      <ChatHeader__SessionInfo $visible={!!(activeAgent && activeSessionId)}>
        Session: {activeSessionId}
      </ChatHeader__SessionInfo>

      {activeAgent && (
        <ChatHeader__Right>
          {showVizButtons && (
            <>
            <ChatHeader__DiagramBtn
              onClick={() => onVisualize('chart')}
              disabled={vizLoading}
              title="Create diagram"
            >
                {vizLoading ? <LoadingOutlined /> : <BarChartOutlined />}
                Create Diagram
              </ChatHeader__DiagramBtn>
              <ChatHeader__ExcelBtn
                onClick={() => onVisualize('excel')}
                disabled={vizLoading}
                title="Export to Excel"
              >
                {vizLoading ? <LoadingOutlined /> : <FileExcelOutlined />}
                Export Excel
              </ChatHeader__ExcelBtn>
            </>
          )}
          <ChatHeader__NewChatBtn onClick={onResetSession} title="New chat">
            <PlusOutlined />
            New Chat
          </ChatHeader__NewChatBtn>
        </ChatHeader__Right>
      )}
    </ChatHeader__Root>
  );
};
